import { useEffect } from 'react'
import { Platform } from 'react-native'
import { usePostHog } from 'posthog-react-native'
import Purchases from 'react-native-purchases'
import * as Sentry from '@sentry/react-native'
import { auth } from './Firebase/firebase'
import { createAuthIdentityHandler } from '../utils/analyticsIdentity'
import { buildProvenanceProperties } from '../utils/buildProvenance'
import { clearRevenueCatIdentity } from '../utils/revenueCatIdentity'

/**
 * ASO-AN-001 — binds Firebase Auth's session to the analytics identity, for the whole app.
 *
 * Before this existed, PostHog identity was a side effect of loading ORGANISATION DATA: the only
 * `identify()` call in the app lived in `AppStack.setUserInfoToAnalytics`, driven by the
 * `organizations/{id}` Firestore snapshot. That put identity several async hops behind
 * authentication — far enough behind that `user_signed_up`, captured in the auth stack, could never
 * be identified (54 signups across five platforms, 2026-08-02…08-29, 0 identified) — and it meant
 * the app had no notion of identity ENDING at all: no `reset()` existed anywhere, so a device kept
 * reporting as the departed user after logout and aliased the next account onto them.
 *
 * This component subscribes to the authentication seam itself, which is the earliest moment a
 * stable Firebase UID exists and the only one that observes every way a session can end (the five
 * `logout()` call sites, a revoked token, a deleted account). It runs inside `PostHogProvider` in
 * Routes.js because `usePostHog()` requires that, and deliberately alongside — not inside —
 * `Routes.setUserDetails`: that function is short-circuited by `signupInProgressAtom` and gated on
 * a Firestore user document, neither of which should be able to delay or veto identity.
 *
 * All of the DECISION-MAKING lives in `createAuthIdentityHandler` (utils/analyticsIdentity.js), not
 * here. This file is only the wiring — the React lifecycle, the platform gate, and the two effect
 * implementations — so that launch, logout and account switch are assertable without mounting a
 * component bound to Firebase, RevenueCat, Sentry and PostHog.
 *
 * `AppStack.setUserInfoToAnalytics` still runs and still enriches the person with org-derived
 * properties. Its `identify()` is now a no-op refinement of an identity already established here,
 * rather than the thing that establishes it. It was left in place on purpose: it is what keeps the
 * existing person properties (email, name, organizationName, role) flowing to production dashboards
 * unchanged, so this fix migrates identity ORDER without silently changing the analytics schema.
 *
 * RevenueCat is cleared here too, and ONLY cleared. Its App User ID is the organizationId (the
 * entitlement is org-owned — see utils/revenueCatIdentity.js) so this bridge, which only knows about
 * people, has no business establishing it; the billing screens do that once an organization is
 * known. But a sign-out must drop it, or the next account on the device inherits the previous
 * organization's entitlement view.
 */
export default function AnalyticsIdentityBridge() {
  const postHog = usePostHog()

  useEffect(() => {
    if (!postHog) return

    const handleAuthUser = createAuthIdentityHandler({
      postHog,
      // ATL-531: `reset()` wipes PostHog's persisted super properties, so the build provenance
      // registered once at PostHogProvider mount has to be put back on every reset edge — nothing
      // else re-runs it inside the life of a JS process.
      superProperties: buildProvenanceProperties(),
      // RevenueCat is only ever configured on iOS in this app (see the billing screens), so there
      // is nothing to clear anywhere else and asking would only risk a native-module error.
      clearSubscriptionIdentity: () =>
        Platform.OS === 'ios' ? clearRevenueCatIdentity({ purchases: Purchases }) : false,
      onError: (error) => {
        console.error('[analytics] identity bridge failed; app continues', error)
        Sentry.captureException(error)
      },
      onIdentitySwitchedWithoutSignOut: () => {
        console.warn('[analytics] identity switched without an intervening sign-out')
      },
    })

    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      void handleAuthUser(authUser)
    })

    return unsubscribe
  }, [postHog])

  return null
}
